import React from "react";

import { useState, useCallback, useMemo } from "react";
// import {
//   Box,
//   Checkbox,
//   Typography,
//   Collapse,
//   IconButton,
//   Paper,
//   InputBase,
// } from "@mui/material";
// import { ChevronRight, ExpandMore, Search } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  Collapse,
  IconButton,
  InputBase,
  Paper,
  Typography,
} from "@mui/material";
import { ArrowDown2, ArrowRight2, SearchNormal1 } from "iconsax-reactjs";

import { ReactComponent as Tick } from "../../build/images/newTabulationAssets/Checkbox Tick.svg";
import { getIdFromPathId } from "../../utils/tabulationUtils";
import { toast } from "react-toastify";

// ─── Dados de exemplo ────────────────────────────────────────────────────────

const TREE_DATA = [
  {
    id: "apresentacao",
    label: "Apresentação",
    children: [
      {
        id: "marcacao",
        label: "Marcação",
        children: [
          {
            id: "consultas",
            label: "Consultas",
            children: [{ id: "consultas2", label: "Consultas2" }],
          },
          { id: "exames", label: "Exames" },
          { id: "procedimentos", label: "Procedimentos" },
        ],
      },
      { id: "visitas", label: "Visitas" },
    ],
  },
  { id: "clientes", label: "Clientes" },
  { id: "desenvolvimento", label: "Desenvolvimento" },
  { id: "empresas", label: "Empresas" },
  { id: "suporte", label: "Suporte" },
];

// ─── Utilitários ─────────────────────────────────────────────────────────────

function getAllDescendantIds(node) {
  if (!(node.children && node.children.length)) return [node.id];
  return [node.id, ...node.children.flatMap(getAllDescendantIds)];
}

function getAllIds(nodes) {
  return nodes.flatMap(getAllDescendantIds);
}

/**
 * Constrói um mapa id → nó com toda a árvore achatada,
 * permitindo recuperar label e demais informações a partir do id.
 */
function buildNodeMap(nodes, map = new Map()) {
  for (const node of nodes) {
    map.set(node.id, node);
    if (node.children && node.children.length) {
      buildNodeMap(node.children, map);
    }
  }
  return map;
}

function getNodeCheckState(node, checked) {
  if (!(node.children && node.children.length)) {
    return checked.has(node.id) ? "checked" : "unchecked";
  }
  const childStates = node.children.map((c) => getNodeCheckState(c, checked));
  const allChecked = childStates.every((s) => s === "checked");
  const noneChecked = childStates.every((s) => s === "unchecked");
  if (allChecked) return "checked";
  if (noneChecked) return "unchecked";
  return "indeterminate";
}

/**
 * Filtra a árvore mantendo:
 * - nós cujo label bate com o termo
 * - todos os filhos de um nó que bate (pai encontrado → mostra tudo abaixo)
 * - o caminho completo até qualquer nó que bate (pai exposto)
 *
 * Retorna { filteredTree, matchedIds }
 * matchedIds = set de ids que devem ficar expandidos para revelar os resultados
 */
function filterTree(nodes, term) {
  if (!term.trim()) return { filteredTree: nodes, matchedIds: new Set() };

  const lower = term.toLowerCase();
  const matchedIds = new Set();

  function walk(nodes) {
    const result = [];

    for (const node of nodes) {
      const selfMatches = node.label.toLowerCase().includes(lower);

      if (selfMatches) {
        // Pai bate → inclui o nó inteiro (com todos os filhos originais)
        result.push(node);
        // Expande este nó para mostrar os filhos
        matchedIds.add(node.id);
      } else if (node.children && node.children.length) {
        // Busca recursiva nos filhos
        const filteredChildren = walk(node.children);
        if (filteredChildren.length > 0) {
          // Algum filho bateu → inclui o pai com apenas os filhos filtrados
          result.push({ ...node, children: filteredChildren });
          // Expande este nó para revelar o caminho
          matchedIds.add(node.id);
        }
      }
    }

    return result;
  }

  return { filteredTree: walk(nodes), matchedIds };
}

// ─── Componente de nó recursivo ──────────────────────────────────────────────

function TreeNodeItem({
  node,
  depth,
  checked,
  expanded,
  onCheck,
  onToggleExpand,
  notAlloweds = [],
}) {
  const hasChildren = !!(node.children && node.children.length);
  const isExpanded = expanded.has(node.id);
  const checkState = getNodeCheckState(node, checked);

  const isToDisable = useMemo(() => {
    if (!Boolean(node)) return false;
    return notAlloweds.some(
      (defaultId) => getIdFromPathId(defaultId) === getIdFromPathId(node.id),
    );
  }, [notAlloweds, node]);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          pl: depth * 2.5,
          pr: 1,
          py: 0.25,
          borderRadius: 1,
          "&:hover": { bgcolor: "action.hover" },
          minHeight: 36,
        }}
      >
        {hasChildren ? (
          <IconButton
            size="small"
            onClick={() => onToggleExpand(node.id)}
            sx={{ p: 0.25, mr: 0.5, color: "text.secondary" }}
          >
            {isExpanded ? <ArrowDown2 size={18} /> : <ArrowRight2 size={18} />}
          </IconButton>
        ) : (
          <Box sx={{ width: 28 }} />
        )}

        {!hasChildren && (
          <Checkbox
            size="small"
            disabled={isToDisable}
            checked={isToDisable ? true : checkState === "checked"}
            indeterminate={checkState === "indeterminate"}
            onChange={() => onCheck(node, checkState)}
            icon={
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "0.25rem",
                  border: "1px solid",
                  borderColor: "text.disabled",
                }}
              />
            }
            checkedIcon={
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "0.25rem",
                  bgcolor: isToDisable ? "#B0B8C9" : "#395ABF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                }}
              >
                <Tick size={14} color="currentColor" variant="Bold" />
              </Box>
            }
            indeterminateIcon={
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "0.25rem",
                  bgcolor: "#395ABF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 2,
                    bgcolor: "#fff",
                    borderRadius: 1,
                  }}
                />
              </Box>
            }
            sx={{ p: 0.5 }}
          />
        )}

        <Typography
          variant="body2"
          sx={{
            userSelect: "none",
            ml: 0.5,
            cursor: hasChildren ? "pointer" : "default",
            fontFamily: "'Inter', sans-serif",
            fontWeight: 500,
            fontSize: "0.75rem",
            lineHeight: "100%",
          }}
          onClick={() => hasChildren && onToggleExpand(node.id)}
        >
          {node.label}
        </Typography>
      </Box>

      {hasChildren && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          {node.children.map((child) => (
            <TreeNodeItem
              key={child.id}
              node={child}
              depth={depth + 1}
              checked={checked}
              expanded={expanded}
              onCheck={onCheck}
              onToggleExpand={onToggleExpand}
              notAlloweds={notAlloweds}
            />
          ))}
        </Collapse>
      )}
    </Box>
  );
}

// ─── Componente principal ────────────────────────────────────────────────────

export default function TreeViewCheckbox({
  data = TREE_DATA,
  notAlloweds = ["consultas", "exames", "procedimentos", "consultas2"],
  onChange = () => {},
  verifyMaxNumberOfSelects = null,
}) {
  const [checked, setChecked] = useState(() => new Set());
  const [expanded, setExpanded] = useState(() => new Set());
  const [search, setSearch] = useState("");

  // Mapa id → nó para recuperar label e demais infos dos itens selecionados
  const nodeMap = useMemo(() => buildNodeMap(data), [data]);

  // Filtra a árvore e calcula quais nós abrir para revelar resultados
  const { filteredTree, matchedIds } = useMemo(
    () => filterTree(data, search),
    [data, search],
  );

  // Quando a busca muda, abre automaticamente o caminho até os resultados
  const effectiveExpanded = useMemo(() => {
    if (!search.trim()) return expanded;
    // União dos expandidos pelo usuário + os que precisam abrir por causa da busca
    return new Set([...expanded, ...matchedIds]);
  }, [search, expanded, matchedIds]);

  const handleCheck = useCallback(
    (node, currentState) => {
      const isAllowed = verifyMaxNumberOfSelects
        ? verifyMaxNumberOfSelects(currentState, node)
        : true;
      if (!isAllowed) {
        toast.error("número máximo de seleção alcançado", { autoClose: 2000 });
        return;
      }
      const descendants = getAllDescendantIds(node);
      setChecked((prev) => {
        const next = new Set(prev);
        if (currentState === "checked") {
          descendants.forEach((id) => next.delete(id));
        } else {
          descendants.forEach((id) => next.add(id));
        }
        // Apenas os nós selecionados, com todas as suas informações
        const selectedNodes = [...next]
          .map((id) => nodeMap.get(id))
          .filter(Boolean);
        onChange(selectedNodes);
        return next;
      });
    },
    [onChange, nodeMap],
  );

  const handleToggleExpand = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const allIds = useMemo(() => getAllIds(data), [data]);
  const allChecked = allIds.every((id) => checked.has(id));
  const noneChecked = allIds.every((id) => !checked.has(id));
  const someChecked = !allChecked && !noneChecked;

  return (
    <Paper
      variant="outlined"
      sx={{
        overflow: "hidden",
        width: "100%",
        border: "none",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
      }}
    >
      {/* Busca */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          borderRadius: "0.5rem",
          border: "0.0625rem solid #CFD8E2",
          height: "2.5rem",
          paddingLeft: "0.75rem",
          gap: "0.5rem",
        }}
      >
        <SearchNormal1 size="16" color="#757575" variant="Outline" />
        <InputBase
          placeholder="Buscar..."
          value={search}
          onChange={handleSearchChange}
          sx={{
            flex: 1,
            fontSize: "0.75rem",
            fontFamily: "'Inter', sans-serif",
            fontWeight: 400,
            lineHeight: "150%",
          }}
        />
      </Box>

      {/* Selecionar tudo */}
      {/* {isToShowSelectAll && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 1,
            py: 0.25,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Checkbox
            size="small"
            checked={allChecked}
            indeterminate={someChecked}
            onChange={() => {
              const next = allChecked ? new Set() : new Set(allIds);
              setChecked(next);
              onChange([...next]);
            }}
            sx={{
              "&.Mui-checked, &.MuiCheckbox-indeterminate": {
                color: "primary.main",
              },
            }}
          />
          <Typography variant="body2" color="text.secondary">
            Selecionar tudo
          </Typography>
        </Box>
      )} */}

      {/* Árvore */}
      <Box
        sx={{
          py: 0.5,
          px: 0.5,
          border: "1px solid #CFD8E2",
          borderRadius: "0.5rem",
          padding: "8px",
        }}
      >
        {filteredTree.length === 0 ? (
          <Typography
            variant="body2"
            color="text.disabled"
            sx={{ py: 2, textAlign: "center" }}
          >
            Nenhum resultado encontrado
          </Typography>
        ) : (
          filteredTree.map((node) => (
            <TreeNodeItem
              key={node.id}
              node={node}
              depth={0}
              checked={checked}
              expanded={effectiveExpanded}
              onCheck={handleCheck}
              onToggleExpand={handleToggleExpand}
              notAlloweds={notAlloweds}
            />
          ))
        )}
      </Box>
    </Paper>
  );
}
